import React from 'react';
import { FieldRenderProps } from '@progress/kendo-react-form';
import {
  DAY_BTN_ACTIVE,
  DAY_BTN_INACTIVE,
  DAY_FULL,
  DAY_SHORT,
  initFromValue,
  inputClass,
  isOver,
  MAX_ERROR_TEXT,
  parseClamped,
  toTimeStr,
  WorkWeekValue,
} from './helpers';

export interface WorkScheduleProps
  extends Omit<Partial<FieldRenderProps>, 'onChange' | 'value'> {
  onChange: (e: { value: WorkWeekValue }) => void;
  value?: WorkWeekValue;
  variant?: 'fixed' | 'rotational' | 'reduced'; // 'fixed' (default) = single-week schedule. 'rotational' = multi-week rotation picker. 'reduced' = reduced-schedule picker.
  processedProps?: boolean;
}

const DAY_BTN_BASE =
  'tw-inline-flex tw-items-center tw-px-4 tw-py-1.5 tw-rounded-full tw-text-sm tw-font-semibold tw-border tw-border-solid';

// Component
type FixedScheduleProps = {
  onChange: WorkScheduleProps['onChange'];
  value?: WorkScheduleProps['value'];
  onBlur?: WorkScheduleProps['onBlur'];
  processedProps?: WorkScheduleProps['processedProps'];
  embedded?: boolean;
};

export function FixedSchedule({
  onChange,
  value,
  onBlur,
  processedProps,
  embedded = false,
}: Readonly<FixedScheduleProps>) {
  const init = React.useMemo(
    () => initFromValue(value),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Tracks the last value we emitted so incoming echoes of our own value
  // don't clobber in-progress input during re-hydration.
  const lastEmittedRef = React.useRef<string | undefined>(undefined);

  const [selectedDays, setSelectedDays] = React.useState(init.selectedDays);
  const [globalHrs, setGlobalHrs] = React.useState('');
  const [globalMins, setGlobalMins] = React.useState('');

  // Re-initialise state when the incoming form value changes, e.g. when
  // returning to a form that has hydrated previously saved answers.
  React.useEffect(() => {
    if (!processedProps) return;
    const incoming = JSON.stringify(value?.weeks ?? null);
    // Skip the echo of the value we just emitted ourselves.
    if (incoming === lastEmittedRef.current) return;
    const next = initFromValue(value);
    setSelectedDays(next.selectedDays);
    setPerDayHrs(next.perDayHrs);
    setPerDayMins(next.perDayMins);
    setGlobalHrs('');
    setGlobalMins('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedProps, value]);

  const [perDayHrs, setPerDayHrs] = React.useState(init.perDayHrs);
  const [perDayMins, setPerDayMins] = React.useState(init.perDayMins);
  const [validationError, setValidationError] = React.useState('');

  // Derived state
  const hasGlobal =
    (Number.parseInt(globalHrs) || 0) + (Number.parseInt(globalMins) || 0) > 0;
  const hasPerDay = selectedDays.some(
    (s, i) =>
      s &&
      ((Number.parseInt(perDayHrs[i]) || 0) > 0 ||
        (Number.parseInt(perDayMins[i]) || 0) > 0),
  );

  // Either/Or mode: entering data in one section disables the other.
  let mode: 'global' | 'perDay' | 'none' = 'none';
  if (hasGlobal) {
    mode = 'global';
  } else if (hasPerDay) {
    mode = 'perDay';
  }

  const anyDaySelected = selectedDays.some(Boolean);

  // Emit onChange whenever relevant state changes
  React.useEffect(() => {
    const week = Array.from({ length: 7 }, (_, i) => {
      if (!selectedDays[i]) return '00:00';
      if (mode === 'global') return toTimeStr(globalHrs, globalMins);
      if (mode === 'perDay') return toTimeStr(perDayHrs[i], perDayMins[i]);
      return '00:00';
    });
    lastEmittedRef.current = JSON.stringify([week]);
    onChange({ value: { weeks: [week] } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDays, globalHrs, globalMins, perDayHrs, perDayMins]);

  // Total calculation
  const totalMins = Array.from({ length: 7 }, (_, i) => {
    if (!selectedDays[i]) return 0;
    if (mode === 'global')
      return parseClamped(globalHrs, 24) * 60 + parseClamped(globalMins, 59);
    if (mode === 'perDay')
      return (
        parseClamped(perDayHrs[i], 24) * 60 + parseClamped(perDayMins[i], 59)
      );
    return 0;
  }).reduce((a, b) => a + b, 0);

  const totalH = Math.floor(totalMins / 60);
  const totalM = totalMins % 60;
  const totalStr =
    totalM > 0 ? `${totalH} hrs ${totalM} mins/week` : `${totalH} hrs/week`;

  // Event handlers
  const toggleDay = (i: number) => {
    const ns = [...selectedDays];
    if (ns[i]) {
      // Deselecting — clear this day's per-day values
      const nh = [...perDayHrs];
      nh[i] = '';
      const nm = [...perDayMins];
      nm[i] = '';
      setPerDayHrs(nh);
      setPerDayMins(nm);
    }
    ns[i] = !ns[i];
    setSelectedDays(ns);
  };

  const removeDay = (i: number) => {
    const ns = [...selectedDays];
    ns[i] = false;
    const nh = [...perDayHrs];
    nh[i] = '';
    const nm = [...perDayMins];
    nm[i] = '';
    setSelectedDays(ns);
    setPerDayHrs(nh);
    setPerDayMins(nm);
  };

  const onGlobalHrs = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    const n = Number.parseInt(v);
    if (v !== '' && !Number.isNaN(n) && n > 24) {
      setValidationError(MAX_ERROR_TEXT);
    } else {
      setValidationError('');
    }
    setGlobalHrs(v);
  };

  const onGlobalMins = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    const n = Number.parseInt(v);
    if (v !== '' && !Number.isNaN(n) && n > 59) {
      setValidationError(MAX_ERROR_TEXT);
    } else {
      setValidationError('');
    }
    setGlobalMins(v);
  };

  const onPerDayHrs =
    (i: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      const n = Number.parseInt(v);
      if (v !== '' && !Number.isNaN(n) && n > 24) {
        setValidationError(MAX_ERROR_TEXT);
      } else {
        setValidationError('');
      }
      const nh = [...perDayHrs];
      if (hasGlobal) {
        const nm = [...perDayMins];
        selectedDays.forEach((selected, j) => {
          if (selected && j !== i) {
            nh[j] = globalHrs;
            nm[j] = globalMins;
          }
        });
        setGlobalHrs('');
        setGlobalMins('');
        setPerDayMins(nm);
      }
      nh[i] = v;
      setPerDayHrs(nh);
    };

  const onPerDayMins =
    (i: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      const n = Number.parseInt(v);
      if (v !== '' && !Number.isNaN(n) && n > 59) {
        setValidationError(MAX_ERROR_TEXT);
      } else {
        setValidationError('');
      }
      const nm = [...perDayMins];
      if (hasGlobal) {
        const nh = [...perDayHrs];
        selectedDays.forEach((selected, j) => {
          if (selected && j !== i) {
            nh[j] = globalHrs;
            nm[j] = globalMins;
          }
        });
        setGlobalHrs('');
        setGlobalMins('');
        setPerDayHrs(nh);
      }
      nm[i] = v;
      setPerDayMins(nm);
    };

  // Render
  const handleBlur = (e: React.FocusEvent<HTMLFieldSetElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      onBlur?.();
    }
  };

  return (
    <fieldset
      id={embedded ? undefined : 'form-work-schedule'}
      className="tw-w-full tw-border-0 tw-p-0 tw-m-0"
      onBlur={handleBlur}
    >
      <div
        className={`tw-rounded-[8px] ${
          embedded ? '' : 'tw-shadow-[0px_2px_4px_0px_rgba(2,84,126,0.20)]'
        }`}
      >
        {/* Section 1 – Day-of-week toggle buttons*/}
        <div
          className={`tw-bg-white tw-rounded-t-[8px] ${
            !anyDaySelected ? 'tw-rounded-b-[8px]' : ''
          } tw-px-5 tw-pt-4 tw-pb-5`}
        >
          <p className="tw-text-base tw-text-unm-adhoc2 tw-mb-3">
            Select your working days and enter hours for each day.
          </p>
          <div className="tw-flex tw-flex-wrap tw-gap-2 sm:tw-flex-nowrap">
            {DAY_SHORT.map((day, i) => (
              <button
                key={day}
                type="button"
                id={`form-work-schedule-day-${i}`}
                aria-pressed={selectedDays[i]}
                onClick={() => toggleDay(i)}
                className={`${DAY_BTN_BASE} ${
                  selectedDays[i] ? DAY_BTN_ACTIVE : DAY_BTN_INACTIVE
                }`}
              >
                {day}
              </button>
            ))}
          </div>
        </div>

        {/* Section 2 – "Same hours each day" global input*/}
        {anyDaySelected && (
          <div className="tw-bg-white tw-px-5 tw-py-4 tw-border-t tw-border-b tw-border-unm-border1">
            <div className="tw-flex tw-items-center tw-gap-2 sm:tw-gap-3 lg:tw-gap-4 tw-w-full">
              <span className="md:tw-flex-1 tw-min-w-0 md:tw-text-right tw-text-base tw-text-unm-adhoc2 md:tw-w-full">
                Same total hours each day
              </span>
              <input
                type="number"
                id="form-work-schedule-global-hrs"
                min={0}
                max={24}
                value={globalHrs}
                placeholder="0"
                onChange={onGlobalHrs}
                className={inputClass(false, isOver(globalHrs, 24))}
                aria-label="Same hours each day – hours"
              />
              <span className="tw-text-base tw-text-unm-adhoc2 tw-whitespace-nowrap">
                hrs
              </span>
              <input
                type="number"
                id="form-work-schedule-global-mins"
                min={0}
                max={59}
                value={globalMins}
                placeholder="00"
                onChange={onGlobalMins}
                className={inputClass(false, isOver(globalMins, 59))}
                aria-label="Same hours each day – minutes"
              />
              <span className="tw-text-base tw-text-unm-adhoc2 tw-whitespace-nowrap">
                mins
              </span>
            </div>
          </div>
        )}

        {/* Section 3 – Per-day rows*/}
        {anyDaySelected && (
          <div className="tw-bg-white tw-rounded-b-[8px] tw-px-5 tw-pt-0 tw-pb-4">
            <p className="tw-text-base tw-text-unm-adhoc2 tw-mb-1 tw-h-10 tw-flex tw-items-end tw-w-full">
              You may enter hours per day below
            </p>
            <div className="tw-flex tw-flex-col tw-gap-3 tw-px-5 tw-py-2">
              {DAY_SHORT.map((_, i) => {
                if (!selectedDays[i]) return null;
                return (
                  <div
                    key={DAY_SHORT[i]}
                    id={`form-work-schedule-row-${i}`}
                    className="tw-flex tw-items-center tw-gap-2 lg:tw-gap-4 tw-w-full"
                  >
                    <span className="tw-w-[44px] tw-shrink-0 tw-text-base tw-font-semibold tw-text-unm-adhoc2 sm:tw-hidden">
                      {DAY_SHORT[i]}
                    </span>
                    <span className="tw-hidden sm:tw-block tw-w-[90px] tw-shrink-0 tw-text-base tw-font-semibold tw-text-unm-adhoc2">
                      {DAY_FULL[i]}
                    </span>
                    <input
                      type="number"
                      id={`form-work-schedule-day-hrs-${i}`}
                      min={0}
                      max={24}
                      value={mode === 'global' ? globalHrs : perDayHrs[i]}
                      placeholder="0"
                      onChange={onPerDayHrs(i)}
                      className={inputClass(
                        false,
                        isOver(
                          mode === 'global' ? globalHrs : perDayHrs[i],
                          24,
                        ),
                      )}
                      aria-label={`${DAY_FULL[i]} hours`}
                    />
                    <span className="tw-text-base tw-text-unm-adhoc2 tw-whitespace-nowrap">
                      hrs
                    </span>
                    <input
                      type="number"
                      id={`form-work-schedule-day-mins-${i}`}
                      min={0}
                      max={59}
                      value={mode === 'global' ? globalMins : perDayMins[i]}
                      placeholder="00"
                      onChange={onPerDayMins(i)}
                      className={inputClass(
                        false,
                        isOver(
                          mode === 'global' ? globalMins : perDayMins[i],
                          59,
                        ),
                      )}
                      aria-label={`${DAY_FULL[i]} minutes`}
                    />
                    <span className="tw-text-base tw-text-unm-adhoc2 tw-whitespace-nowrap">
                      mins
                    </span>
                    <button
                      type="button"
                      id={`form-work-schedule-remove-${i}`}
                      onClick={() => removeDay(i)}
                      className="tw-ml-auto tw-shrink-0 tw-text-base tw-text-unm-interactive01-400 tw-bg-transparent tw-border-0 tw-cursor-pointer hover:tw-underline"
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
            </div>
            <p className="tw-text-base tw-text-unm-adhoc2 tw-mt-3">
              Total: <strong>{totalStr}</strong>
            </p>
            {validationError && (
              <p
                id="form-work-schedule-validation-error"
                className="tw-mt-2 tw-text-sm tw-font-semibold tw-text-[#B42318]"
              >
                {validationError}
              </p>
            )}
          </div>
        )}
      </div>
    </fieldset>
  );
}
